import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  CptReconciliationEntry,
  CptReconciliationStatus,
  ConfirmationMethod,
  DocumentationConfidence,
  ReconciliationSummary,
  mapDbRowToEntry,
  isValidTransition,
  isCptBillable,
  calculateReconciliationSummary,
} from '@/lib/reconciliation/cptStateMachine';

interface UseCptReconciliationOptions {
  authorizationId: string | null;
  authorizedCpts: string[];
  cptDescriptions?: Record<string, string>;
  isDemoMode?: boolean;
}

export function useCptReconciliation({
  authorizationId,
  authorizedCpts,
  cptDescriptions = {},
  isDemoMode = false,
}: UseCptReconciliationOptions) {
  const [entries, setEntries] = useState<CptReconciliationEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch existing reconciliation entries
  const fetchEntries = useCallback(async () => {
    if (!authorizationId) return;

    setIsLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('cpt_reconciliation_entries')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('cpt_code', { ascending: true });

      if (fetchError) throw fetchError;

      setEntries((data || []).map(mapDbRowToEntry));
    } catch (err) {
      console.error('Failed to fetch reconciliation entries:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [authorizationId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Initialize entries for all authorized CPTs
  const initializeEntries = useCallback(async (): Promise<boolean> => {
    if (!authorizationId || authorizedCpts.length === 0) return false;

    setIsInitializing(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Determine status based on demo mode
      const initialStatus: 'authorized' | 'confirmed_performed' = isDemoMode 
        ? 'confirmed_performed' 
        : 'authorized';

      // Create entries for each authorized CPT
      const entriesToInsert = authorizedCpts.map(cptCode => ({
        authorization_id: authorizationId,
        cpt_code: cptCode,
        cpt_description: cptDescriptions[cptCode] || null,
        status: initialStatus,
        surgeon_confirmed: isDemoMode,
        surgeon_confirmed_at: isDemoMode ? new Date().toISOString() : null,
        surgeon_confirmed_by: isDemoMode ? user?.id : null,
        surgeon_confirmation_method: (isDemoMode ? 'demo_auto' : null) as 'direct' | 'clarification_question' | 'demo_auto' | null,
        is_billable: isDemoMode,
        billable_determined_at: isDemoMode ? new Date().toISOString() : null,
        billable_reason: isDemoMode ? 'Demo mode auto-confirmation' : null,
        is_demo_entry: isDemoMode,
      }));

      const { error: insertError } = await supabase
        .from('cpt_reconciliation_entries')
        .upsert(entriesToInsert, {
          onConflict: 'authorization_id,cpt_code',
        });

      if (insertError) throw insertError;

      // Log audit entries for initialization
      if (user) {
        const auditNewStatus: 'authorized' | 'confirmed_performed' = isDemoMode 
          ? 'confirmed_performed' 
          : 'authorized';
          
        const auditEntries = authorizedCpts.map(cptCode => ({
          reconciliation_entry_id: crypto.randomUUID(),
          authorization_id: authorizationId,
          cpt_code: cptCode,
          action: isDemoMode ? 'Demo auto-confirmed' : 'Initialized for reconciliation',
          previous_status: null as null,
          new_status: auditNewStatus,
          actor_id: user.id,
          actor_type: isDemoMode ? 'system' : 'link_ops',
          reason: isDemoMode ? 'Demo mode auto-confirmation' : 'Case entered reconciliation phase',
          details: { is_demo: isDemoMode },
        }));

        // Audit log table removed — skip
        console.debug('Skipping cpt_reconciliation_audit_log (table removed)');
      }

      await fetchEntries();
      return true;
    } catch (err) {
      console.error('Failed to initialize reconciliation entries:', err);
      setError(err as Error);
      return false;
    } finally {
      setIsInitializing(false);
    }
  }, [authorizationId, authorizedCpts, cptDescriptions, isDemoMode, fetchEntries]);

  // Update CPT status with surgeon confirmation
  const confirmCpt = useCallback(async (
    cptCode: string,
    newStatus: CptReconciliationStatus,
    method: ConfirmationMethod,
    reason?: string
  ): Promise<boolean> => {
    if (!authorizationId) return false;

    const entry = entries.find(e => e.cptCode === cptCode);
    if (!entry) {
      setError(new Error(`No reconciliation entry found for CPT ${cptCode}`));
      return false;
    }

    // Validate transition
    if (!isValidTransition(entry.status, newStatus)) {
      setError(new Error(`Invalid transition from ${entry.status} to ${newStatus}`));
      return false;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const billable = isCptBillable(newStatus);

      // Update the entry
      const { error: updateError } = await supabase
        .from('cpt_reconciliation_entries')
        .update({
          status: newStatus,
          previous_status: entry.status,
          surgeon_confirmed: newStatus === 'confirmed_performed',
          surgeon_confirmed_at: newStatus === 'confirmed_performed' ? now : null,
          surgeon_confirmed_by: newStatus === 'confirmed_performed' ? user?.id : null,
          surgeon_confirmation_method: method,
          is_billable: billable,
          billable_determined_at: now,
          billable_reason: reason || `Status changed to ${newStatus}`,
          is_demo_entry: method === 'demo_auto',
        })
        .eq('id', entry.id);

      if (updateError) throw updateError;

      // Audit log table removed — skip
      console.debug('Skipping cpt_reconciliation_audit_log (table removed)');

      // Update local state
      setEntries(prev => prev.map(e =>
        e.id === entry.id
          ? {
              ...e,
              status: newStatus,
              previousStatus: entry.status,
              surgeonConfirmed: newStatus === 'confirmed_performed',
              surgeonConfirmedAt: newStatus === 'confirmed_performed' ? now : null,
              surgeonConfirmedBy: newStatus === 'confirmed_performed' ? user?.id || null : null,
              surgeonConfirmationMethod: method,
              isBillable: billable,
              billableDeterminedAt: now,
              billableReason: reason || `Status changed to ${newStatus}`,
              isDemoEntry: method === 'demo_auto',
            }
          : e
      ));

      return true;
    } catch (err) {
      console.error('Failed to confirm CPT:', err);
      setError(err as Error);
      return false;
    }
  }, [authorizationId, entries]);

  // Batch confirm all CPTs as performed (for demo mode)
  const confirmAllCpts = useCallback(async (): Promise<boolean> => {
    if (!authorizationId) return false;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const now = new Date().toISOString();

      const confirmedStatus: 'confirmed_performed' = 'confirmed_performed';
      const confirmationMethod: 'demo_auto' = 'demo_auto';
      
      // Update all pending entries
      const { error: updateError } = await supabase
        .from('cpt_reconciliation_entries')
        .update({
          status: confirmedStatus,
          surgeon_confirmed: true,
          surgeon_confirmed_at: now,
          surgeon_confirmed_by: user?.id,
          surgeon_confirmation_method: confirmationMethod,
          is_billable: true,
          billable_determined_at: now,
          billable_reason: 'Demo mode batch confirmation',
          is_demo_entry: true,
        })
        .eq('authorization_id', authorizationId)
        .eq('status', 'authorized');

      if (updateError) throw updateError;

      await fetchEntries();
      return true;
    } catch (err) {
      console.error('Failed to confirm all CPTs:', err);
      setError(err as Error);
      return false;
    }
  }, [authorizationId, fetchEntries]);

  // Update documentation analysis results
  const updateDocumentationAnalysis = useCallback(async (
    cptCode: string,
    found: boolean,
    confidence: DocumentationConfidence,
    matchedText?: string
  ): Promise<boolean> => {
    const entry = entries.find(e => e.cptCode === cptCode);
    if (!entry) return false;

    try {
      const { error: updateError } = await supabase
        .from('cpt_reconciliation_entries')
        .update({
          documentation_found: found,
          documentation_confidence: confidence,
          matched_text: matchedText || null,
        })
        .eq('id', entry.id);

      if (updateError) throw updateError;

      setEntries(prev => prev.map(e =>
        e.id === entry.id
          ? {
              ...e,
              documentationFound: found,
              documentationConfidence: confidence,
              matchedText: matchedText || null,
            }
          : e
      ));

      return true;
    } catch (err) {
      console.error('Failed to update documentation analysis:', err);
      setError(err as Error);
      return false;
    }
  }, [entries]);

  // Calculate summary
  const summary: ReconciliationSummary = calculateReconciliationSummary(entries);

  // Check if entries are initialized
  const isInitialized = entries.length > 0;
  const needsInitialization = !isInitialized && authorizedCpts.length > 0;

  return {
    entries,
    summary,
    isLoading,
    isInitializing,
    error,
    isInitialized,
    needsInitialization,
    fetchEntries,
    initializeEntries,
    confirmCpt,
    confirmAllCpts,
    updateDocumentationAnalysis,
  };
}
